import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

// pdf.js 4.8 WorkerTransport.getPage throws synchronously (messageHandler is
// null) when react-pdf's Document has already destroyed the old document but a
// <Page> still holds it. react-pdf only catches rejected promises, so the
// TypeError escapes the Page effect.
function isDestroyedPdfTransportError(error: unknown): boolean {
  return (
    error instanceof TypeError && error.message.includes('sendWithPromise')
  );
}

interface PdfPageErrorBoundaryProps {
  /** Shown for the harmless destroyed-document race. */
  fallback: ReactNode;
  /** Shown for any other page error, so the user is not left with a blank gap. */
  errorFallback: ReactNode;
  /** Changes on each successful document load, which clears a caught error. */
  resetKey: number;
  children: ReactNode;
}

/**
 * Keeps a page that throws from reaching the app-level error boundary, which
 * would blank the whole viewer. Shows `fallback` (destroyed document) or
 * `errorFallback` (anything else) until the next document loads.
 */
export class PdfPageErrorBoundary extends Component<
  PdfPageErrorBoundaryProps,
  { hasError: boolean; isDestroyedDocument: boolean }
> {
  state = { hasError: false, isDestroyedDocument: false };

  static getDerivedStateFromError(error: unknown) {
    return {
      hasError: true,
      isDestroyedDocument: isDestroyedPdfTransportError(error),
    };
  }

  componentDidUpdate(prevProps: PdfPageErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, isDestroyedDocument: false });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isDestroyedPdfTransportError(error)) {
      console.warn(
        'PdfPageErrorBoundary: page rendered against a destroyed PDF document; showing placeholder until the next document load',
        error
      );
      return;
    }
    Sentry.captureReactException(error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return this.state.isDestroyedDocument
      ? this.props.fallback
      : this.props.errorFallback;
  }
}
